/**
 * useApi Hook
 * Provides API fetch functionality with Clerk authentication
 */
import { useCallback, useMemo } from "react";
import { useAuth } from "@clerk/react-router";
import { useAdminPreviewContext } from "@/contexts/admin-preview-context";
import { ApiException, apiFetch, classifyAuthError } from "@/services/api";
import { logError } from "@/services/error-logging-service";

export { ApiException };

// Report the auth-expiry 401 to the backend at most once per page load — the retry storm
// after a dead session would otherwise spam the log endpoint (whose calls also 401).
let hasReportedAuthExpiry = false;

function reportAuthExpiry(endpoint: string, error: ApiException): void {
    if (hasReportedAuthExpiry) return;
    hasReportedAuthExpiry = true;
    // Best-effort: if the session is truly dead this call fails silently too,
    // but the backend already logs every 401 as a Warning on its side.
    void logError({
        operation: "AuthSessionExpired",
        errorMessage: `401 persisted after token refresh retry: ${error.message}`,
        metadata: { endpoint, code: error.code, numericCode: error.numericCode },
        severity: "warning",
        category: "Authentication",
    });
}

/**
 * Hook that provides authenticated API fetch functionality
 * Uses Clerk's getToken() to automatically attach Bearer tokens to requests
 */
export function useApi() {
    const { getToken } = useAuth();
    const { previewClientId } = useAdminPreviewContext();

    /**
     * Make an authenticated API request
     * Automatically attaches Clerk token as Bearer Authorization header.
     * When inside an admin preview session, also attaches X-Preview-Client-Id header.
     *
     * On an auth-expiry 401 the request is retried once with a freshly minted token
     * (skipCache) — this covers the common "token lapsed between mint and validation"
     * race without destroying the session. Only if the retry also fails does the
     * error surface to callers.
     */
    const fetchWithAuth = useCallback(
        async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
            // Get fresh token from Clerk for backend API calls
            const token = await getToken({ template: "jwt-template" });
            const previewHeaders: Record<string, string> = previewClientId != null ? { "X-Preview-Client-Id": String(previewClientId) } : {};
            const mergedOptions: RequestInit = {
                ...options,
                headers: {
                    ...previewHeaders,
                    ...(options.headers as Record<string, string> | undefined),
                },
            };
            try {
                return await apiFetch<T>(endpoint, mergedOptions, token);
            } catch (error) {
                // Safe to retry any verb: a middleware 401 means the request was rejected
                // before the function body executed, so nothing was processed.
                if (classifyAuthError(error) !== "session-expired") throw error;

                const freshToken = await getToken({ template: "jwt-template", skipCache: true }).catch(() => null);
                if (!freshToken) {
                    reportAuthExpiry(endpoint, error as ApiException);
                    throw error;
                }
                try {
                    return await apiFetch<T>(endpoint, mergedOptions, freshToken);
                } catch (retryError) {
                    if (classifyAuthError(retryError) === "session-expired") {
                        reportAuthExpiry(endpoint, retryError as ApiException);
                    }
                    throw retryError;
                }
            }
        },
        [getToken, previewClientId],
    );

    /**
     * GET request with authentication
     */
    const get = useCallback(
        async <T>(endpoint: string): Promise<T> => {
            return fetchWithAuth<T>(endpoint, { method: "GET" });
        },
        [fetchWithAuth],
    );

    /**
     * POST request with authentication
     */
    const post = useCallback(
        async <T>(endpoint: string, data?: unknown): Promise<T> => {
            return fetchWithAuth<T>(endpoint, {
                method: "POST",
                body: data ? JSON.stringify(data) : undefined,
            });
        },
        [fetchWithAuth],
    );

    /**
     * POST request with FormData (for file uploads)
     * Content-Type is automatically handled by apiFetch for FormData
     */
    const postFormData = useCallback(
        async <T>(endpoint: string, formData: FormData): Promise<T> => {
            return fetchWithAuth<T>(endpoint, {
                method: "POST",
                body: formData,
            });
        },
        [fetchWithAuth],
    );

    /**
     * PUT request with authentication
     */
    const put = useCallback(
        async <T>(endpoint: string, data?: unknown): Promise<T> => {
            return fetchWithAuth<T>(endpoint, {
                method: "PUT",
                body: data ? JSON.stringify(data) : undefined,
            });
        },
        [fetchWithAuth],
    );

    /**
     * PATCH request with authentication
     */
    const patch = useCallback(
        async <T>(endpoint: string, data?: unknown): Promise<T> => {
            return fetchWithAuth<T>(endpoint, {
                method: "PATCH",
                body: data ? JSON.stringify(data) : undefined,
            });
        },
        [fetchWithAuth],
    );

    /**
     * DELETE request with authentication
     * Supports optional JSON body (e.g. batch delete endpoints)
     */
    const del = useCallback(
        async <T>(endpoint: string, data?: unknown): Promise<T> => {
            return fetchWithAuth<T>(endpoint, {
                method: "DELETE",
                body: data ? JSON.stringify(data) : undefined,
            });
        },
        [fetchWithAuth],
    );

    /**
     * Get a fresh auth token from Clerk
     * Useful when you need to pass the token to other services
     */
    const getAuthToken = useCallback(async (): Promise<string | null> => {
        return getToken({ template: "jwt-template" });
    }, [getToken]);

    return useMemo(
        () => ({
            fetchWithAuth,
            get,
            post,
            postFormData,
            put,
            patch,
            delete: del,
            getAuthToken,
        }),
        [fetchWithAuth, get, post, postFormData, put, patch, del, getAuthToken],
    );
}
